/**
 * Category suggestion.
 *
 * Entirely local and entirely deterministic: the same merchant always suggests
 * the same category, with no network call and no model. Precedence is
 *   1. user rules, highest priority first
 *   2. what the user themselves chose for this merchant before
 *   3. built-in Malaysian keyword rules
 *   4. the default category
 */
import { merchantKey, sameMerchant } from './merchant.js';
import type { MerchantRule, TransactionType } from './schemas.js';
import { DEFAULT_CATEGORY_ID, TRANSFER_CATEGORY_ID } from './categories.js';

export interface BuiltInRule {
  categoryId: string;
  /** Matched against the merchant key (lowercase, alphanumeric words). */
  keywords: readonly string[];
}

export const BUILT_IN_RULES: readonly BuiltInRule[] = [
  {
    categoryId: 'food-and-drinks',
    keywords: [
      'mcdonald',
      'kfc',
      'burger king',
      'subway',
      'starbucks',
      'zus coffee',
      'zus',
      'coffee bean',
      'gong cha',
      'tealive',
      'chagee',
      'kopitiam',
      'kopi',
      'mamak',
      'restoran',
      'restaurant',
      'cafe',
      'kafe',
      'bakery',
      'roti',
      'nasi',
      'satay',
      'sushi',
      'ramen',
      'pizza',
      'domino',
      'foodpanda',
      'grabfood',
      'shopeefood',
      'secret recipe',
      'old town white',
      'texas chicken',
      'marrybrown',
      'llaollao',
      'baskin',
      'family mart',
      'familymart',
    ],
  },
  {
    categoryId: 'groceries',
    keywords: [
      'tesco',
      'lotus',
      'aeon',
      'jaya grocer',
      'village grocer',
      'mydin',
      'giant',
      'nsk',
      'econsave',
      'speedmart',
      '99 speedmart',
      'ben s independent',
      'hero market',
      'sam s groceria',
      'grocer',
      'pasar',
      'market',
      'mini market',
      '7 eleven',
      'seven eleven',
      'kk super mart',
      'kk mart',
    ],
  },
  {
    categoryId: 'transport',
    keywords: [
      'grab',
      'grabcar',
      'maxim',
      'indriver',
      'airasia ride',
      'touch n go',
      'tng reload',
      'smart tag',
      'plus highway',
      'plus toll',
      'toll',
      'rapidkl',
      'rapid kl',
      'mrt',
      'lrt',
      'ktm',
      'ktmb',
      'monorail',
      'petronas',
      'shell',
      'petron',
      'caltex',
      'bhpetrol',
      'bhp',
      'parking',
      'parkir',
      'jomparking',
      'flexi parking',
      'wilson parking',
      'car wash',
      'workshop',
    ],
  },
  {
    categoryId: 'bills',
    keywords: [
      'tnb',
      'tenaga nasional',
      'syabas',
      'air selangor',
      'indah water',
      'unifi',
      'tm unifi',
      'telekom malaysia',
      'maxis',
      'celcom',
      'celcomdigi',
      'digi',
      'umobile',
      'u mobile',
      'yes 4g',
      'time internet',
      'astro',
      'insurance',
      'takaful',
      'prudential',
      'aia',
      'great eastern',
      'allianz',
      'quit rent',
      'cukai',
      'assessment',
      'management fee',
      'maintenance fee',
    ],
  },
  {
    categoryId: 'shopping',
    keywords: [
      'shopee',
      'lazada',
      'zalora',
      'uniqlo',
      'h m',
      'cotton on',
      'padini',
      'brands outlet',
      'mr diy',
      'mrdiy',
      'ikea',
      'ace hardware',
      'harvey norman',
      'senheng',
      'courts',
      'watsons',
      'guardian',
      'sephora',
      'apple store',
      'switch',
      'machines',
      'decathlon',
      'popular',
      'mph',
      'kinokuniya',
    ],
  },
  {
    categoryId: 'entertainment',
    keywords: [
      'gsc',
      'golden screen',
      'tgv',
      'mbo cinema',
      'cinema',
      'cineplex',
      'karaoke',
      'redbox',
      'steam',
      'playstation',
      'nintendo',
      'xbox',
      'escape room',
      'bowling',
      'zoo negara',
      'aquaria',
      'sunway lagoon',
      'genting',
    ],
  },
  {
    categoryId: 'health',
    keywords: [
      'klinik',
      'clinic',
      'hospital',
      'pantai',
      'gleneagles',
      'sunway medical',
      'columbia asia',
      'kpj',
      'pharmacy',
      'farmasi',
      'big pharmacy',
      'caring pharmacy',
      'alpro',
      'dentist',
      'dental',
      'optical',
      'specsavers',
      'physio',
      'gym',
      'fitness first',
      'celebrity fitness',
      'anytime fitness',
    ],
  },
  {
    categoryId: 'education',
    keywords: [
      'university',
      'universiti',
      'college',
      'kolej',
      'school',
      'sekolah',
      'tuition',
      'tuisyen',
      'coursera',
      'udemy',
      'skillshare',
      'duolingo',
      'book store',
      'bookstore',
    ],
  },
  {
    categoryId: 'travel',
    keywords: [
      'airasia',
      'malaysia airlines',
      'batik air',
      'firefly',
      'scoot',
      'agoda',
      'booking com',
      'airbnb',
      'traveloka',
      'klook',
      'hotel',
      'resort',
      'hostel',
      'homestay',
      'expedia',
    ],
  },
  {
    categoryId: 'subscriptions',
    keywords: [
      'netflix',
      'spotify',
      'youtube premium',
      'google one',
      'google storage',
      'icloud',
      'apple com bill',
      'apple services',
      'disney',
      'viu',
      'iqiyi',
      'wetv',
      'hbo',
      'adobe',
      'microsoft 365',
      'office 365',
      'dropbox',
      'notion',
      'openai',
      'anthropic',
      'github',
      'canva',
      'figma',
    ],
  },
  {
    categoryId: 'transfers',
    keywords: [
      'duitnow transfer',
      'transfer to',
      'instant transfer',
      'ibg transfer',
      'reload',
      'top up',
      'topup',
      'own account',
      'credit card payment',
    ],
  },
];

export interface SuggestCategoryInput {
  merchant: string;
  type?: TransactionType;
  /** User-defined rules, checked before anything else. */
  rules?: readonly MerchantRule[];
  /** Merchant -> category that the user has confirmed in the past. */
  history?: readonly { merchant: string; categoryId: string; count: number }[];
  defaultCategoryId?: string;
}

export interface CategorySuggestion {
  categoryId: string;
  /** Where the suggestion came from -- surfaced in the UI so it is never a black box. */
  reason: 'user-rule' | 'history' | 'keyword' | 'transfer-type' | 'default';
  confidence: number;
  /** The rule or keyword that matched, for the explanation line. */
  matchedOn?: string;
}

function ruleMatches(rule: MerchantRule, key: string): boolean {
  const pattern = merchantKey(rule.pattern) || rule.pattern.trim().toLowerCase();
  if (!pattern) return false;
  switch (rule.matchType) {
    case 'equals':
      return key === pattern;
    case 'starts-with':
      return key.startsWith(pattern);
    case 'contains':
    default:
      return key.includes(pattern);
  }
}

/** Deterministic category suggestion. Same inputs, same answer, every time. */
export function suggestCategory(input: SuggestCategoryInput): CategorySuggestion {
  const {
    merchant,
    type = 'expense',
    rules = [],
    history = [],
    defaultCategoryId = DEFAULT_CATEGORY_ID,
  } = input;

  const key = merchantKey(merchant);

  // 1. User rules. Highest priority number wins; ties break on id for stability.
  const applicable = rules
    .filter((rule) => rule.enabled !== false && ruleMatches(rule, key))
    .sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id));
  const winner = applicable[0];
  if (winner) {
    return {
      categoryId: winner.categoryId,
      reason: 'user-rule',
      confidence: 0.98,
      matchedOn: winner.pattern,
    };
  }

  // A transfer is a transfer whatever the merchant says.
  if (type === 'transfer') {
    return { categoryId: TRANSFER_CATEGORY_ID, reason: 'transfer-type', confidence: 0.95 };
  }

  // 2. What the user picked for this merchant before.
  if (key) {
    const matches = history
      .filter((entry) => sameMerchant(entry.merchant, merchant))
      .sort((a, b) => b.count - a.count || a.categoryId.localeCompare(b.categoryId));
    const best = matches[0];
    if (best) {
      return {
        categoryId: best.categoryId,
        reason: 'history',
        confidence: best.count >= 3 ? 0.9 : 0.75,
        matchedOn: best.merchant,
      };
    }
  }

  // 3. Built-in keywords. Longest keyword wins so "tng reload" beats "tng".
  if (key) {
    let best: { categoryId: string; keyword: string } | undefined;
    for (const rule of BUILT_IN_RULES) {
      for (const keyword of rule.keywords) {
        if (!key.includes(keyword)) continue;
        if (!best || keyword.length > best.keyword.length) {
          best = { categoryId: rule.categoryId, keyword };
        }
      }
    }
    if (best) {
      return {
        categoryId: best.categoryId,
        reason: 'keyword',
        confidence: best.keyword.length >= 6 ? 0.8 : 0.65,
        matchedOn: best.keyword,
      };
    }
  }

  return { categoryId: defaultCategoryId, reason: 'default', confidence: 0.3 };
}

/** Apply a rule's optional rename, used when a rule tidies an ugly descriptor. */
export function applyRuleRename(
  merchant: string,
  rules: readonly MerchantRule[],
): string | undefined {
  const key = merchantKey(merchant);
  const match = rules
    .filter((rule) => rule.enabled !== false && rule.renameTo && ruleMatches(rule, key))
    .sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id))[0];
  return match?.renameTo ?? undefined;
}
